import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button,Skeleton,Stack } from "@mui/material";
import { ThemeProvider } from '@mui/material/styles';
import { useMutation,useQuery } from "@tanstack/react-query";
import MUIDataTable,{ MUIDataTableOptions } from "mui-datatables";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { RiDeleteBin5Line,RiPencilFill } from "react-icons/ri";
import { toast } from "react-toastify";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import NovoChamado from "../../service/NovoChamado";
import queryClient from "../../service/query";
import type { newTicketObjType } from "../../types/tickets/types";
import { getMuiTheme,tableOptions } from '../TableHelpers/options';
import { StyledDiv } from "../TableHelpers/TableStyle";
import NewTicketModal from "./Modal/NewTicketModal";

const initialValue: newTicketObjType = {
  name: '',
  message: '',
  serviceType: '',
  daysLimit: null,
  timeLimit: null,
  setsList: [],
  redirectable: false,
  aprove: false,
  canReopen: false,
  responsible: null,
  permissions: {
    rejectRule: null,
    cancelRule: null,
    completionRule: null,
    closeRule: null,
    reopenRule: null,
    aproveRule: null,
    changePriorityRule: null,
  },
  prioritySLA: null,
};

const chamadoService = new NovoChamado();

export default function NewTickets() {
  const [openModal, setOpenModal] = useState(false);
  const [ticketEdit, setTicketEdit] = useState<newTicketObjType | undefined>(initialValue);

  const { t } = useTranslation('translation');

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['GET_MODEL_LIST'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token não encontrado');
        if (token) {
          const response = await chamadoService.getList(token);
          if (!response) throw new Error('Erro ao buscar modelos de chamado');
          return response;
        }
      } catch (err) {
        throw new Error('Erro ao buscar modelos de chamado');
      }
    },
    retry: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });


  const { mutate, isPending } = useMutation({
    mutationKey: ['DELETE_TICKET'],
    mutationFn: async (value: string) => {
      try {
        const promise = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error('Token não encontrado');
          if (token) {
            const response = await chamadoService.deleteModel(token, value);
            if (!response || typeof response === 'number') throw new Error('Erro ao remover modelo de chamado');
            return response;
          }
        }, {
          pending: `${t('notifications.ticket.RemovendoModelo')}`,
          success: `${t('notifications.ticket.Modeloremovido')}`,
          error: `${t('notifications.ticket.Erroaoremover')}`
        }, { toastId: 'deleteTicket' });
        return promise;
      } catch (err) {
        throw new Error('Erro ao remover modelo de chamado');
      }
    },
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ['GET_MODEL_LIST'] });
    }
  });

  const onLoading = isLoading || isFetching || isPending;

  const columns = [
    {
      name: "name",
      label: t('name'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "message",
      label: t('message'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: t('edit'),
      options: {
        customBodyRender: (value: string,) => {
          return (
            <Button
              endIcon={<RiPencilFill />}
              onClick={() => {
                trackEventMatomo('tela de chamado 2.0', 'click', 'button', 'abre modal');
                setOpenModal(!openModal);
                const ticket = data?.find((elem: newTicketObjType) => elem.id === value);
                setTicketEdit(ticket);
              }}
              className="button"
            >
              {t('edit')}
            </Button>
          );
        },
        filter: true,
        sort: false,
      }
    },
    {
      name: "id",
      label: t('delete'),
      options: {
        customBodyRender: (value: string) => {
          return (
            <Button
              endIcon={<RiDeleteBin5Line />}
              onClick={() => {
                trackEventMatomo('tela de chamado 2.0', 'click', 'button', 'deleta');
                mutate(value);
              }}
              disabled={isPending}
              variant="contained"
              color="error"
            >
              {t('delete')}
            </Button>
          );
        },
        filter: true,
        sort: false,
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  return (
    <>
      {openModal &&
        <NewTicketModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          ticketEdit={ticketEdit}
          setTicketEdit={setTicketEdit}
        />}
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
        width={'100%'}
      >
        <Button
          disabled={onLoading}
          endIcon={<AddCircleIcon />}
          className="button is-primary"
          type="button"
          name='add'
          onClick={() => {
            trackEventMatomo('tela de chamado 2.0', 'click', 'button', 'abre modal');
            setTicketEdit(initialValue);
            setOpenModal(!openModal);
          }}
        >
          {t('notifications.ticket.novoModelo')}
        </Button>
        <Stack
          justifyContent="flex-start"
          alignItems="stretch"
          width={'100%'}
        >
          {onLoading
            ? <Skeleton variant="rounded" width={'100%'} height={400} />
            : <StyledDiv>
              <ThemeProvider theme={getMuiTheme()}>
                <MUIDataTable
                  title={t('notifications.ticket.chamadoModeloNovo')}
                  data={data ?? []}
                  columns={columns}
                  options={{ ...tableOptions, ...translatedTextLabels }}
                />
              </ThemeProvider>
            </StyledDiv>}
        </Stack>
      </Stack>
    </>
  );
}
